import { ChangeEventHandler, useEffect, useState } from 'react'
import { css } from '@emotion/react'
import { PaginationData } from '@/types/pagination'
import { UseFetchResult } from '@/types/useFetchResult'
import { useFetch } from '@/hooks/useFetch'
import { showError } from '@/shared/toast'
import { MainTitle } from '@/components/title/MainTitle'
import { SearchForm } from '../SearchForm'
import { BaseTable, type Column } from '@/components/table/BaseTable'
import { BasePagination } from '@/components/pagination/BasePagination'
import { PaginationInfo } from '@/components/pagination/PaginationInfo'
import { DestinationsList } from '@/types/destinationsList'

const modalStyle = css`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 500;
`

const modalContentStyle = css`
  position: relative;
  background: white;
  width: 50%;
  height: 95vh;
  overflow-y: auto;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
`

const closeButtonStyle = css`
  position: absolute;
  top: 12px;
  right: 12px;
  background: transparent;
  border: none;
  font-size: 18px;
  cursor: pointer;
`

const tableContainerStyle = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 16px;
`

const paginationContainerStyle = css`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
`
const columns: Column<DestinationsList>[] = [
  {
    header: '適用開始日',
    text: (row) =>
      new Date(row.startDate).toLocaleDateString('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }),
    width: '20%',
    sortKey: 'start_date',
    divStyle: {
      textAlign: 'center',
      whiteSpace: 'nowrap',
    },
  },
  {
    header: '送達先番号',
    text: (row) => row.destinationCode,
    width: '20%',
    sortKey: 'destination_code',
    divStyle: {
      textAlign: 'center',
      whiteSpace: 'nowrap',
    },
  },
  {
    header: '送達先名',
    text: (row) => row.name,
    width: '45%',
  },
]

type Props = {
  onClose: () => void
  onSelect: (destinationCode: string, name: string) => void
  startDate?: string
}

export const DestinationModal = ({ onClose, onSelect, startDate }: Props) => {
  const [pagination, setPagination] = useState<
    PaginationData<DestinationsList>
  >({
    data: [],
    currentPage: 1,
    lastPage: 1,
    total: 0,
    from: 0,
    to: 0,
    perPage: 30,
  })
  const [keyword, setKeyword] = useState('')

  const handleChange: ChangeEventHandler<
    HTMLInputElement | HTMLSelectElement
  > = (event) => {
    const { name, value } = event.target
    if (name === 'keyword') {
      setKeyword(value)
    }
  }
  const handleClick = () => {
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }))

    refetch()
  }

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: page,
    }))

    refetch()
  }
  const { data, error, refetch }: UseFetchResult = useFetch(
    `/api/destinations?page=${pagination.currentPage}&per_page=${pagination.perPage}&keyword=${keyword}&start_date=${startDate}`,
  )

  useEffect(() => {
    if (data && 'items' in data) {
      setPagination((prev) => ({
        ...prev,
        data: data.items,
        currentPage: data.pagination.currentPage,
        lastPage: data.pagination.lastPage,
        total: data.pagination.total,
        from: data.pagination.from,
        to: data.pagination.to,
        perPage: data.pagination.perPage,
      }))
    }
  }, [data])

  useEffect(() => {
    if (error) {
      showError('送達先一覧の取得に失敗しました。')
    }
  }, [error])
  return (
    <div css={modalStyle}>
      <div css={modalContentStyle}>
        <button css={closeButtonStyle} onClick={onClose} aria-label="閉じる">
          ×
        </button>
        <div css={tableContainerStyle}>
          <MainTitle>送達先の選択</MainTitle>
          <SearchForm
            helpText={
              '下記の項目を対象に部分一致検索を行います' +
              '\n' +
              '「送達先番号」「送達先名」のいずれか'
            }
            onChange={handleChange}
            onClick={handleClick}
            keyword={keyword}
          />
          <BaseTable
            data={pagination.data}
            columns={columns}
            isDelete={false}
            onSelect={(row) => {
              onSelect(
                (row as DestinationsList).destinationCode,
                (row as DestinationsList).name,
              )
              onClose()
            }}
          />
          <div css={paginationContainerStyle}>
            <PaginationInfo
              from={pagination.from}
              to={pagination.to}
              total={pagination.total}
            />
            <BasePagination
              currentPage={pagination.currentPage}
              lastPage={pagination.lastPage}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
